import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CurrencyContext from 'contexts/currencyContext'
import InlineIcon from 'components/utils/InlineIcon'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useLuxPlusSubscriptionProduct from 'luxPlus/hooks/useLuxPlusSubscriptionProduct'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'

interface Props {
  onCheckAvailability?: () => void
}

function MaldivesReturnForFreeInfoModal({ onCheckAvailability }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const navigateTo = useNavigateTo()
  const currencyCode = useContext(CurrencyContext)
  const isMember = useAppSelector(getCanRedeemLuxPlusBenefits)

  // Non-members are quoted the subscription price, so the product needs fetching.
  useLuxPlusSubscriptionProduct(!isMember)
  const subscriptionPrice = useAppSelector(state => state.luxPlus.products.subscription.offers?.[0]?.price)

  // The yearly price only appears once the subscription product has loaded, so the copy
  // has a variant that reads correctly without it.
  const nonMemberBenefitCopy = subscriptionPrice === undefined
    ? intl.formatMessage({
        id: 'MSIgxDdvRv70kZZtGghz',
        defaultMessage: "Free return flights are a LuxPlus+ benefit. Join and you'll unlock this saving, plus member pricing on every escape after it.",
        description: 'Explanation that the free return flight requires LuxPlus membership, used before the subscription price has loaded',
      })
    : intl.formatMessage({
        id: '7ACNw-8vsfyGnzeCzLrx',
        defaultMessage: "Free return flights are a LuxPlus+ benefit. Join for {price} a year and you'll unlock this saving, plus member pricing on every escape after it.",
        description: 'Explanation that the free return flight requires LuxPlus membership, including the yearly subscription price',
      }, { price: intl.formatNumber(subscriptionPrice, { style: 'currency', currency: currencyCode, maximumFractionDigits: 0 }) })

  const handleCheckAvailability = useCallback(() => {
    modalElement?.resolve()
    onCheckAvailability?.()
  }, [modalElement, onCheckAvailability])

  const handleJoin = useCallback(() => {
    modalElement?.resolve()
    navigateTo(`/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`)
  }, [modalElement, navigateTo])

  return (
    <ModalBase size="S" mode="default" dismissible>
      <ModalHeader
        title={intl.formatMessage({
          id: '2O8wtNIfhYpbbTdhpFCg',
          defaultMessage: 'Maldives return for free flight sale',
          description: 'Title of the modal explaining how the Maldives return for free flight campaign works',
        })}
      />
      <ModalBody>
        <Sheet background="normal-background" padding={24}>
          <Group direction="vertical" gap={20}>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading6" as="h3">
                {intl.formatMessage({
                  id: 'BwpDBCo_TwVivwv6isHI',
                  defaultMessage: 'How it works',
                  description: 'Heading of the section explaining how the Maldives return for free campaign applies to a booking',
                })}
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'VQIC7-6rDkfEHVnRl7Ru',
                  defaultMessage: "Book a Maldives hotel and flights package on an eligible date and you'll pay nothing for the flight home. The saving's already included in the price you see.",
                  description: 'Explanation of how the Maldives return for free campaign discount is applied to an eligible booking',
                })}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={4}>
              <Heading variant="heading6" as="h3">
                <FormattedMessage
                  id="iVJS1x118esGI9VGNTlQ"
                  defaultMessage="Where to find free return flights {flame}"
                  description="Heading of the section telling the customer how to spot dates that include a free return flight"
                  values={{ flame: <InlineIcon Icon={SolidFireIcon} colour="lux-plus" /> }}
                />
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: '7BqkplpLz6K8GIsmVrO_',
                  defaultMessage: 'Look for the blue flame in the calendar. It marks the months and dates where free return seats are still available.',
                  description: 'Explanation that the blue flame in the Maldives charter calendar marks dates with a free return flight',
                })}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={4}>
              <Heading variant="heading6" as="h3">
                {isMember
                  ? intl.formatMessage({
                      id: 'dacn6OoP8hZt03ORYPL4',
                      defaultMessage: 'LuxPlus+ member exclusive',
                      description: 'Heading of the membership section of the return for free modal, shown to existing LuxPlus members',
                    })
                  : intl.formatMessage({
                      id: 'okOqkjqyoNbBJBSfQG7F',
                      defaultMessage: 'Exclusive to LuxPlus+ members',
                      description: 'Heading of the membership section of the return for free modal, shown to customers who are not LuxPlus members',
                    })}
              </Heading>
              {isMember && <BodyText variant="medium">
                {intl.formatMessage({
                  id: '1G3JNgEKDTHwrchRKAG8',
                  defaultMessage: "You're a LuxPlus+ member, so the free return flight is built into every eligible price you see. Nothing extra to do at checkout.",
                  description: 'Reassurance for existing LuxPlus members that the free return flight needs no action at checkout',
                })}
              </BodyText>}
              {!isMember && <>
                <BodyText variant="medium">{nonMemberBenefitCopy}</BodyText>
                <div>
                  <TextLink onClick={handleJoin} weight="bold">
                    {intl.formatMessage({
                      id: 'oiklmuc-ZQWLnJZ7Qzh2',
                      defaultMessage: 'Join LuxPlus+',
                      description: 'Link in the return for free modal taking a non-member to the LuxPlus membership page',
                    })}
                  </TextLink>
                </div>
              </>}
            </Group>

            <div>
              <TextLink to="/promotions" weight="regular">
                {intl.formatMessage({
                  id: 'tmYQbH2WPsVfYvPyprP3',
                  defaultMessage: 'T&Cs apply',
                  description: 'Link in the return for free modal through to the promotion terms and conditions',
                })}
              </TextLink>
            </div>
          </Group>
        </Sheet>
      </ModalBody>

      {!!onCheckAvailability && <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'WsDBcY5s-HF6Qd5_dOPz',
            defaultMessage: 'Check availability',
            description: 'Primary button in the return for free modal, which does the same thing as the call to action on the surface that opened it',
          }),
          onClick: handleCheckAvailability,
        }}
      />}
    </ModalBase>
  )
}

export default MaldivesReturnForFreeInfoModal
